import {
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerProps,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Spacer,
  Stack,
  Switch,
} from '@chakra-ui/react';
import { TransactionType, useSaveApproverSettingsMutation } from 'api';
import { ClientResponse } from 'api/bindings/ClientResponse';
import { CoreTransactionRoutingSettings } from 'api/bindings/CoreTransactionRoutingSettings';
import { TransactionRoutingSettingsModel } from 'api/bindings/TransactionRoutingSettingsModel';
import { TransactionRoutingStep } from 'api/bindings/TransactionRoutingStep';
import { AutoComplete, FieldError, FormButtonGroup } from 'features/components';
import { every } from 'lodash';
import { useCallback, useMemo } from 'react';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { FiPlus, FiTrash } from 'react-icons/fi';
import { getDisplayName } from 'types';

interface Form extends Omit<TransactionRoutingSettingsModel, 'steps'> {
  steps: StepSelection[];
}

interface StepSelection extends TransactionRoutingStep {
  type: 'user' | 'role';
}

export interface ClientApproversFormDrawerProps
  extends Omit<DrawerProps, 'children'> {
  client: ClientResponse | undefined;
  heading: string;
  // stepSet: StepSetTbl | undefined;
  transactionType: TransactionType;
  settings: CoreTransactionRoutingSettings | undefined;
  isOpen: boolean;
  assigneeApprovalOptional: boolean;
  // onSubmit: (form: Form) => Promise<any>;
}

export const ClientApproversFormDrawer = (
  props: ClientApproversFormDrawerProps
) => {
  const {
    client,
    heading,
    settings,
    // stepSet,
    transactionType,
    assigneeApprovalOptional,
    onClose,
    // isOpen,
    // onSubmit,
    ...rest
  } = props;
  const [postApprovers] = useSaveApproverSettingsMutation();
  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {
      clientId: client?.id ?? '0',
      transactionType,
      assigneeApprovalRequired: settings?.assigneeApprovalRequired,
      steps: settings?.stepSet?.steps.map((x) => ({
        ...x,
        type: (x.userId && 'user') || 'role',
      })),
    },
  });
  const { handleSubmit, control } = form;

  const stepsFieldArray = useFieldArray({
    name: 'steps',
    control,
    keyName: '_id',
    rules: {
      validate: {
        required: (v) =>
          assigneeApprovalOptional ||
          v.length > 0 ||
          'At least one approver is required',
        fullySpecified: (v) =>
          every(v, (v) => (v.type === 'user' ? !!v.userId : !!v.roleId)) ||
          'All required approvals must specify either a user or role',
      },
    },
  });

  const roles = useMemo(
    () => client?.roles.map((x) => ({ id: x.id, name: x.name })),
    [client]
  );
  const users = useMemo(
    () => client?.users.map((x) => ({ id: x.id, name: getDisplayName(x) })),
    [client]
  );

  const onSubmit = useCallback(
    async (form: Form) => {
      try {
        const result = await postApprovers(form as any); // TODO: Hack, need to consider migrating approvers lambda to rust
        onClose();
        return result;
      } catch (err: any) {
        throw err;
      }
    },
    [postApprovers, onClose]
  );
  return (
    <Drawer size="lg" onClose={onClose} {...rest}>
      <DrawerOverlay />
      <DrawerContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormProvider {...form}>
            <DrawerHeader>{heading}</DrawerHeader>
            <DrawerBody>
              <Stack spacing={4} divider={<Divider />}>
                {assigneeApprovalOptional && (
                  <FormControl>
                    <FormLabel htmlFor="asignee">
                      Asignee Approval Required
                    </FormLabel>
                    <Controller
                      control={control}
                      name="assigneeApprovalRequired"
                      render={(p) => (
                        <Switch
                          size="lg"
                          isChecked={p.field.value}
                          onChange={() => p.field.onChange(!p.field.value)}
                        />
                      )}
                    />
                  </FormControl>
                )}

                <Flex pb={8}>
                  <Spacer />
                  <Button
                    colorScheme="blue"
                    variant="solid"
                    leftIcon={
                      // @ts-ignore
                      <FiPlus />
                    }
                    aria-label="New Approver"
                    onClick={() =>
                      stepsFieldArray.append({
                        id: '0',
                        stepSetId: '0',
                        type: !!roles?.length ? 'role' : 'user',
                        userId: '',
                        roleId: '',
                      })
                    }
                  >
                    New Approver
                  </Button>
                </Flex>
                {stepsFieldArray.fields.map((row, i) => (
                  <Stack spacing={8}>
                    <FormControl>
                      <RadioGroup
                        defaultValue={roles?.length ? 'role' : 'user'}
                        value={row.type}
                        onChange={(type) =>
                          stepsFieldArray.update(i, {
                            ...row,
                            type: type as 'user' | 'role',
                          })
                        }
                      >
                        <Stack direction="row" spacing={4}>
                          <Radio
                            colorScheme="blue"
                            value="role"
                            size="lg"
                            isDisabled={!roles?.length}
                          >
                            Role
                          </Radio>
                          <Radio colorScheme="blue" value="user" size="lg">
                            User
                          </Radio>
                        </Stack>
                      </RadioGroup>
                    </FormControl>
                    <Flex>
                      <FormControl>
                        <FormLabel>Approver:</FormLabel>
                        <AutoComplete
                          name=""
                          items={row.type === 'role' ? roles : users}
                          valueKey={(x) => x.id}
                          textKey={(x) => x.name}
                          value={row.type === 'role' ? row.roleId : row.userId}
                          onChange={(v) => {
                            if (row.type === 'role') {
                              stepsFieldArray.update(i, {
                                ...row,
                                roleId: v as string,
                              });
                            } else {
                              stepsFieldArray.update(i, {
                                ...row,
                                userId: v as string,
                              });
                            }
                          }}
                        />
                      </FormControl>
                      <Spacer />
                      <IconButton
                        colorScheme="red"
                        variant={'solid'}
                        icon={
                          // @ts-ignore
                          <FiTrash />
                        }
                        aria-label="Remove Approver"
                        onClick={() => stepsFieldArray.remove(i)}
                        alignSelf="flex-end"
                      />
                    </Flex>
                  </Stack>
                ))}
                <FieldError name="steps" />
              </Stack>
            </DrawerBody>
            <DrawerFooter>
              <FormButtonGroup
                onCancel={onClose}
                onConfirm={handleSubmit(onSubmit)}
              />
            </DrawerFooter>
          </FormProvider>
        </form>
      </DrawerContent>
    </Drawer>
  );
};
